// Authenticated adapter for assistant-ui's native file part. The official File
// primitive owns the output card's outline/compact/MIME-icon presentation;
// this adapter only adds authenticated internal downloads and inline previews.
// Generated image artifacts use AuthenticatedImagePart / assistant-ui image
// parts instead.
// The download endpoint (GET /api/v1/artifacts/{artifact_id}/download)
// requires the same bearer token the shared API client attaches in dev (auth
// is cookie-based in prod, see shared/auth/tokenStorage.ts) — a plain
// `<img src>`/`<a href>` can't set that header, so we fetch the file
// ourselves and hand the browser a blob URL either way.
import { useState } from 'react';
import { Download } from 'lucide-react';
import type { DataMessagePart } from '@assistant-ui/react';
import { artifactDownloadUrl, artifactFetchInit, isImageContentType, pickFileTypeIcon, useAuthenticatedObjectUrl } from '@/entities/file';
import { readEnv } from '@/shared/config';
import { DotMatrix } from '@/shared/ui/dot-matrix';
import { File as AssistantFile } from '@/shared/ui/file';
import { downloadBlob } from '../model/markdownExport';

interface FileOutputData {
  fileId?: string;
  filename?: string;
  contentType?: string;
  sizeBytes?: number;
  downloadUrl?: string;
}

function internalDownloadUrl(fileId: string): string {
  return artifactDownloadUrl(readEnv('VITE_API_BASE_URL') ?? '', fileId);
}

function resolveDownloadUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  const base = readEnv('VITE_API_BASE_URL') ?? globalThis.location?.origin ?? '';
  try {
    return new URL(url, base).toString();
  } catch {
    return url;
  }
}

function isExternalDownloadUrl(url: string | undefined): url is string {
  if (!url || !/^https?:\/\//i.test(url)) return false;
  try {
    if (isInternalDownloadUrl(url)) return false;
    return new URL(url).protocol === 'http:' || new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

function isInternalDownloadUrl(url: string): boolean {
  try {
    return /\/api\/v1\/artifacts\/[^/]+\/download\/?$/.test(new URL(url, globalThis.location?.origin).pathname);
  } catch {
    return /\/api\/v1\/artifacts\/[^/]+\/download\/?$/.test(url);
  }
}

export function AssistantFilePart({ data }: DataMessagePart) {
  const file = (data ?? {}) as FileOutputData;
  const [state, setState] = useState<'idle' | 'loading' | 'gone'>('idle');
  const isImage = isImageContentType(file.contentType);
  const fileId = file.fileId;
  const downloadUrl = resolveDownloadUrl(file.downloadUrl ?? (fileId ? internalDownloadUrl(fileId) : undefined));

  // Images fetch their own bytes once and render inline; a failed/unavailable
  // fetch falls back to the compact type-icon chip.
  const { src: imageUrl, failed: imageFailed } = useAuthenticatedObjectUrl(isImage ? downloadUrl : undefined);

  if (!file.filename || !downloadUrl) return null;

  const Icon = pickFileTypeIcon(file.contentType, file.filename);
  const externalDownloadUrl = isExternalDownloadUrl(file.downloadUrl) ? file.downloadUrl : null;

  async function handleDownload() {
    if (!downloadUrl) return;
    setState('loading');
    try {
      const res = await fetch(downloadUrl, artifactFetchInit(downloadUrl));
      if (res.status === 410) {
        setState('gone');
        return;
      }
      if (!res.ok) {
        setState('idle');
        return;
      }
      const blob = await res.blob();
      downloadBlob(blob, file.filename ?? 'download');
      setState('idle');
    } catch {
      setState('idle');
    }
  }

  if (state === 'gone') {
    return (
      <AssistantFile.Root variant="outline" size="sm" className="mb-2 text-muted-foreground">
        <AssistantFile.Icon mimeType={file.contentType}>
          <Icon className="size-5" />
        </AssistantFile.Icon>
        <AssistantFile.Name className="font-normal">{file.filename} — This file is no longer available.</AssistantFile.Name>
      </AssistantFile.Root>
    );
  }

  // Image: a real inline preview (running -> complete), not just a chip with a
  // thumbnail. Click opens the full-resolution blob in a new tab (a zoom
  // affordance without pulling in a lightbox library); the File primitive below
  // carries the filename/size and an explicit download action.
  if (isImage && !imageFailed) {
    if (!imageUrl) {
      return (
        <div className="mb-2 flex h-40 w-64 max-w-full items-center justify-center rounded-md border border-border/60 bg-muted/40">
          <DotMatrix state="loading" label={`Loading ${file.filename}`} />
        </div>
      );
    }
    return (
      <figure className="mb-2 max-w-full">
        <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="block w-fit max-w-full">
          <img
            src={imageUrl}
            alt={file.filename}
            className="max-h-96 max-w-full cursor-zoom-in rounded-md border border-border/60 object-contain"
          />
        </a>
        <figcaption className="mt-1">
          <AssistantFile.Root variant="outline" size="sm" className="w-full max-w-full text-muted-foreground">
            <AssistantFile.Icon mimeType={file.contentType}>
              <Icon className="size-5" />
            </AssistantFile.Icon>
            <AssistantFile.Name>{file.filename}</AssistantFile.Name>
            {typeof file.sizeBytes === 'number' && <AssistantFile.Size bytes={file.sizeBytes} />}
            <button
              type="button"
              aria-label={`Download ${file.filename}`}
              disabled={state === 'loading'}
              onClick={() => void handleDownload()}
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              {state === 'loading' ? <DotMatrix state="downloading" className="size-3.5" /> : <Download className="size-3.5" />}
            </button>
          </AssistantFile.Root>
        </figcaption>
      </figure>
    );
  }

  return (
    <AssistantFile.Root variant="outline" size="sm" className="mb-2 max-w-full">
      {state === 'loading' ? (
        <DotMatrix state="downloading" className="size-5 shrink-0" />
      ) : (
        <AssistantFile.Icon mimeType={file.contentType}>
          <Icon className="size-5" />
        </AssistantFile.Icon>
      )}
      <AssistantFile.Name>{file.filename}</AssistantFile.Name>
      {typeof file.sizeBytes === 'number' && <AssistantFile.Size bytes={file.sizeBytes} />}
      {externalDownloadUrl ? (
        <AssistantFile.Download
          data={externalDownloadUrl}
          mimeType={file.contentType ?? 'application/octet-stream'}
          filename={file.filename}
          aria-label={`Download ${file.filename}`}
        />
      ) : (
        <button
          type="button"
          aria-label={`Download ${file.filename}`}
          disabled={state === 'loading'}
          onClick={() => void handleDownload()}
          className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
        >
          <Download className="size-4" />
        </button>
      )}
    </AssistantFile.Root>
  );
}
